// The px/module model and decodable floors. Ported from event-connect's scanner
// (scan-geometry.ts) unchanged.
//
// px/module = (camera pixels across the barcode) / 95 (EAN-13's total module count) — set by
// sensor resolution and working distance, not by the ROI crop. This is what separates "the
// camera cannot see enough pixels" from "the decoder is bad".
//
// There is no ISO number behind FLOOR_HARD: ISO/IEC 15416 grades a scan reflectance profile
// and is silent on camera pixels-per-module. FLOOR_HARD cites Google ML Kit's published EAN-13
// guidance ("at least 2 pixels wide" per module, i.e. "at least 190 pixels wide" for the full
// 95-module symbol); zxing-cpp (our engine, via zxing-wasm) hard-fails below 1.0 px/module
// (ODRowReader.h:83-88) — that's the physical bound. FLOOR_MARGINAL is fitted to event-connect's
// field observations (see its scan-geometry.test.ts) and is a *tuning trigger*, not a give-up
// verdict.

const EAN13_MODULES = 95

// ponytail: fitted, not derived — a barcode filling half the ROI width reproduces event-connect's
// field measurements (1080p/720p/480p). Upgrade path: record measuredPxPerModule from real hits
// on this step (already wired below), so the fitted constant can be replaced by real fill data
// once a session has any.
const REALISTIC_FILL = 0.5

/** Below this, the manual-entry prompt is honest — the camera genuinely cannot do it. */
export const FLOOR_HARD = 2.0

/** Between FLOOR_HARD and this, the device is marginal: the tuning trigger, not a verdict. */
export const FLOOR_MARGINAL = 3.0

export function expectedPxPerModule(roiWidthPx: number): number {
  return (roiWidthPx * REALISTIC_FILL) / EAN13_MODULES
}

/** A hit's `boundingBox.width` is already in ROI-canvas pixels (worker-decoder.ts). */
export function measuredPxPerModule(boundingBoxWidthPx: number): number {
  return boundingBoxWidthPx / EAN13_MODULES
}

export function isBelowHardFloor(pxPerModule: number): boolean {
  return pxPerModule < FLOOR_HARD
}

export function isBelowMarginalFloor(pxPerModule: number): boolean {
  return pxPerModule < FLOOR_MARGINAL
}

export type FloorVerdict = 'ok' | 'marginal' | 'hard'

/** Single decision point for both the give-up signal and the actionable "marginal" hint — one
 * number, one place both read it from, so they can never disagree about what state the camera
 * is in. */
export function evaluateFloor(pxPerModule: number): FloorVerdict {
  if (isBelowHardFloor(pxPerModule)) return 'hard'
  if (isBelowMarginalFloor(pxPerModule)) return 'marginal'
  return 'ok'
}
